import type { RollLog, RollTag } from '../../../shared/game/combat-types.js';
import { describeRoll } from '../../../shared/game/roll-log.js';
import { setTooltip } from '../tooltips.js';

const signed = (value: number) => `${value < 0 ? '−' : '+'}${Math.abs(value)}`;

function piece(tag: keyof HTMLElementTagNameMap, className: string, text: string): HTMLElement {
  const node = document.createElement(tag); node.className = className; node.textContent = text;
  return node;
}

/** The kept die that decides the roll tints with its outcome: gilt for a crit, ink-red for a miss or failed save, green for a success. */
function tone(roll: RollLog): string {
  return roll.outcome === 'crit' ? 'crit' : roll.outcome === 'miss' || roll.outcome === 'fail' ? 'bad' : roll.outcome === 'success' ? 'good' : '';
}

function tagNote(tag: RollTag): HTMLElement {
  return piece('span', `log-note log-note-${tag.kind}`, tag.label);
}

/** One roll as a row of die faces and sums: the Primary Die first, dropped dice struck through, exploded dice dashed, then what was added and taken off. */
export function rollRow(roll: RollLog): HTMLElement {
  const row = document.createElement('span'); row.className = 'log-dice';
  row.append(piece('span', 'log-die-label', `${roll.kind === 'heal' && roll.dice.length > 1 ? roll.dice.length : ''}d${roll.sides}`));
  roll.dice.forEach((value, index) => {
    const face = piece('span', index === 0 ? `log-die log-die-primary ${tone(roll)}` : 'log-die', String(value));
    if (index === 0 && roll.primaryFrom !== undefined && roll.primaryFrom !== value) face.prepend(piece('s', 'log-die-from', String(roll.primaryFrom)));
    row.append(face);
  });
  for (const value of roll.dropped) row.append(piece('span', 'log-die dropped', String(value)));
  for (const value of roll.explosions) row.append(piece('span', 'log-die explosion', `✦${value}`));
  if (roll.outcome !== 'miss') {
    for (const term of roll.terms) row.append(piece('span', 'log-term', `${signed(term.value)} ${term.label}`));
    if (roll.terms.length || roll.explosions.length || roll.dice.length > 1) row.append(piece('span', 'log-sum', `= ${roll.total}`));
    for (const term of roll.reductions) row.append(piece('span', term.value < 0 ? 'log-term reduction' : 'log-term', `${signed(term.value)} ${term.label}`));
  }
  if (roll.dc !== undefined) row.append(piece('span', `log-term log-dc ${roll.outcome === 'success' ? 'good' : 'bad'}`, `vs DC ${roll.dc}`));
  for (const tag of roll.tags) row.append(tagNote(tag));
  return row;
}

/** A combat log line: the sentence the server wrote, the dice beneath it, and the whole account in words on hover. */
export function logEntry(roll: RollLog | undefined, formatted: string): HTMLElement {
  const line = document.createElement('p');
  line.innerHTML = formatted;
  if (!roll) return line;
  line.classList.add('has-roll');
  if (roll.kind !== 'initiative') line.append(rollRow(roll));
  setTooltip(line, describeRoll(roll).join('\n'));
  return line;
}
